/* Temporary authored-lesson capture entry (Task #593).
 *
 * The URL grammar is deliberately unambiguous:
 *   beat=<zero-based integer>  selects by position (0 is valid)
 *   beatId=<exact authored id> selects by id
 * Supplying both, duplicate matching ids, or any malformed/out-of-range target
 * is an error.  This module is intentionally independent of the avatar stack.
 */

export const isCaptureRequested = (cfg, search) => {
  if (!cfg || cfg.captureEnabled !== true) return false;
  let q;
  try { q = new URLSearchParams(String(search || "")); } catch (_) { return false; }
  return q.get("demo") === "1" && q.get("autostart") === "1";
};

export function resolveCaptureTarget(lesson, search) {
  const beats = lesson && Array.isArray(lesson.beats) ? lesson.beats : null;
  if (!beats || !beats.length) {
    return { ok: false, error: "This authored lesson has no beats to capture." };
  }
  let q;
  try { q = new URLSearchParams(String(search || "")); }
  catch (_) { return { ok: false, error: "The capture URL could not be read." }; }

  const indexes = q.getAll("beat");
  const ids = q.getAll("beatId");
  if (indexes.length + ids.length === 0) {
    return { ok: false, error: "Choose a target with beat=<zero-based index> or beatId=<exact authored id>." };
  }
  if (indexes.length > 1 || ids.length > 1 || (indexes.length && ids.length)) {
    return { ok: false, error: "The capture target is ambiguous. Supply exactly one beat or beatId." };
  }

  if (indexes.length) {
    const raw = indexes[0];
    if (!/^(0|[1-9]\d*)$/.test(raw)) {
      return { ok: false, error: "beat must be a zero-based whole-number index (0 is the first beat)." };
    }
    const index = Number(raw);
    if (!Number.isSafeInteger(index) || index >= beats.length) {
      return { ok: false, error: "beat index " + raw + " is outside this lesson (0\u2013" + (beats.length - 1) + ")." };
    }
    return { ok: true, index, beat: beats[index], selector: "index", value: raw };
  }

  const id = ids[0];
  if (!id) return { ok: false, error: "beatId must be a non-empty exact authored beat ID." };
  const matches = [];
  beats.forEach((beat, index) => {
    if (beat && typeof beat.id === "string" && beat.id === id) matches.push(index);
  });
  if (!matches.length) return { ok: false, error: 'No authored beat has the exact ID "' + id + '".' };
  if (matches.length > 1) return { ok: false, error: 'The authored beat ID "' + id + '" is duplicated and cannot be captured safely.' };
  return { ok: true, index: matches[0], beat: beats[matches[0]], selector: "id", value: id };
}

export function installCaptureControls(controller) {
  let generation = 0;
  const api = {
    get generation() { return generation; },
    reset() {
      generation += 1;
      if (!controller || typeof controller.resetCapture !== "function") return false;
      return controller.resetCapture(generation) !== false;
    },
  };
  try { window.__lessonCapture = api; } catch (_) {}
  const onKey = (event) => {
    // Clean recording-frame shortcut: Alt+Shift+R. Ignore editable controls.
    if (!event || event.key.toLowerCase() !== "r" || !event.altKey || !event.shiftKey) return;
    const tag = event.target && event.target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
        (event.target && event.target.isContentEditable)) return;
    event.preventDefault();
    api.reset();
  };
  try { window.addEventListener("keydown", onKey); } catch (_) {}
  return api;
}
